// =====================================================================
// Textos sugeridos para as mensagens do pós-venda. Cada bloco oferece de
// 2 a 3 opções ("Cordial", "Direta", …) e só usa as variáveis que aquele
// tipo de mensagem consegue preencher (ver variaveisMensagem.ts — o teste
// textosSugeridos.test.ts garante isso).
// =====================================================================

export type SugestaoTexto = { rotulo: string; texto: string };

/** Ao registrar a ordem de serviço (variáveis da OS). */
export const SUGESTOES_CRIACAO: SugestaoTexto[] = [
  {
    rotulo: 'Cordial',
    texto:
      'Olá {{cliente}}! Recebemos seu {{veiculo}} ({{placa}}) e abrimos a ordem de serviço {{numero}}. Acompanhe o andamento por aqui: {{acompanhamento}}',
  },
  {
    rotulo: 'Direta',
    texto: '{{cliente}}, sua O.S. {{numero}} foi registrada. Acompanhe: {{acompanhamento}}',
  },
  {
    rotulo: 'Com itens',
    texto:
      'Olá {{cliente}}! Registramos a O.S. {{numero}} do seu {{veiculo}}:\n{{itens}}\nTotal: {{total}}. Qualquer dúvida, é só responder esta mensagem.',
  },
];

/** Por etapa padrão do kanban (id da etapa → opções). */
export const SUGESTOES_STATUS: Record<string, SugestaoTexto[]> = {
  EmAberto: [
    {
      rotulo: 'Cordial',
      texto:
        'Olá {{cliente}}! A ordem de serviço {{numero}} do seu {{veiculo}} está aberta. Acompanhe por aqui: {{acompanhamento}}',
    },
    {
      rotulo: 'Direta',
      texto: '{{cliente}}, a O.S. {{numero}} foi aberta. Acompanhe: {{acompanhamento}}',
    },
  ],
  AguardandoPeca: [
    {
      rotulo: 'Cordial',
      texto:
        'Olá {{cliente}}! O serviço do seu {{veiculo}} está aguardando a chegada de peça. Assim que ela chegar, seguimos com o trabalho. Acompanhe: {{acompanhamento}}',
    },
    {
      rotulo: 'Direta',
      texto: '{{cliente}}, a O.S. {{numero}} está aguardando peça. Acompanhe: {{acompanhamento}}',
    },
  ],
  EmServico: [
    {
      rotulo: 'Cordial',
      texto:
        'Olá {{cliente}}! Seu {{veiculo}} ({{placa}}) já está em serviço. Acompanhe o andamento: {{acompanhamento}} 🔧',
    },
    {
      rotulo: 'Direta',
      texto: '{{cliente}}, começamos o serviço da O.S. {{numero}}. Acompanhe: {{acompanhamento}}',
    },
  ],
  ServicoConcluidoAguardandoPagamento: [
    {
      rotulo: 'Cordial',
      texto:
        'Olá {{cliente}}! O serviço do seu {{veiculo}} foi concluído: {{servicos}}. Total: {{total}}. Os detalhes estão aqui: {{acompanhamento}}. É só combinar a retirada com a gente.',
    },
    {
      rotulo: 'Direta',
      texto: '{{cliente}}, a O.S. {{numero}} está pronta. Total: {{total}}. Detalhes: {{acompanhamento}}',
    },
    {
      rotulo: 'Com itens',
      texto:
        'Olá {{cliente}}! Serviço concluído no seu {{veiculo}}:\n{{itens}}\nTotal: {{total}}. Detalhes: {{acompanhamento}}',
    },
  ],
  FechadoPago: [
    {
      rotulo: 'Cordial',
      texto:
        'Obrigado pela confiança, {{cliente}}! A O.S. {{numero}} está encerrada. Se puder, conte como foi o atendimento: {{avaliacao}} 🙂',
    },
    {
      rotulo: 'Direta',
      texto: '{{cliente}}, pagamento da O.S. {{numero}} confirmado. Obrigado!',
    },
    {
      rotulo: 'Com retorno',
      texto:
        'Obrigado, {{cliente}}! Qualquer coisa com o seu {{veiculo}}, é só chamar a gente por aqui.',
    },
  ],
  Cancelado: [
    {
      rotulo: 'Cordial',
      texto:
        'Olá {{cliente}}, a ordem de serviço {{numero}} foi cancelada. Se precisar de algo, é só chamar a gente por aqui.',
    },
    {
      rotulo: 'Direta',
      texto: '{{cliente}}, a O.S. {{numero}} foi cancelada.',
    },
  ],
};

/** Tira chaves do rótulo para ele não virar uma variável sem querer. */
function rotuloSeguro(rotulo: string): string {
  return rotulo.replace(/[{}]/g, '').trim() || 'nova etapa';
}

/** Sugestões genéricas para etapas criadas pela oficina (usa o rótulo). */
export function sugestoesEtapaGenerica(rotulo: string): SugestaoTexto[] {
  const r = rotuloSeguro(rotulo);
  return [
    {
      rotulo: 'Cordial',
      texto: `Olá {{cliente}}! A ordem de serviço {{numero}} do seu {{veiculo}} mudou para "${r}". Acompanhe: {{acompanhamento}}`,
    },
    {
      rotulo: 'Direta',
      texto: `{{cliente}}, O.S. {{numero}}: ${r}. Acompanhe: {{acompanhamento}}`,
    },
  ];
}

/** Sugestões de uma etapa: as próprias, se for padrão; senão as genéricas. */
export function sugestoesParaEtapa(id: string, rotulo: string): SugestaoTexto[] {
  return SUGESTOES_STATUS[id] ?? sugestoesEtapaGenerica(rotulo);
}

/** Aniversário do cliente (só cliente e link). */
export const SUGESTOES_ANIVERSARIO: SugestaoTexto[] = [
  {
    rotulo: 'Cordial',
    texto:
      'Feliz aniversário, {{cliente}}! 🎉 Toda a equipe da oficina deseja a você um ótimo dia.',
  },
  {
    rotulo: 'Direta',
    texto: 'Parabéns, {{cliente}}! Feliz aniversário e um ótimo dia.',
  },
  {
    rotulo: 'Com retorno',
    texto:
      'Feliz aniversário, {{cliente}}! Obrigado por confiar na nossa oficina. Quando precisar, é só chamar a gente por aqui.',
  },
];

/** Lembrete de manutenção (variáveis da última OS do veículo). */
export const SUGESTOES_MANUTENCAO: SugestaoTexto[] = [
  {
    rotulo: 'Cordial',
    texto:
      'Olá {{cliente}}! Já está na hora da próxima revisão do seu {{veiculo}} ({{placa}}). Quer agendar? É só responder esta mensagem.',
  },
  {
    rotulo: 'Direta',
    texto: '{{cliente}}, lembrete de revisão do {{veiculo}} ({{placa}}). Responda para agendar.',
  },
  {
    rotulo: 'Com histórico',
    texto:
      'Olá {{cliente}}! Na última visita ({{data}}) fizemos: {{servicos}}. Que tal agendar a próxima manutenção do seu {{veiculo}}? É só responder esta mensagem. 🔧',
  },
];

/** Pedido de avaliação (o 1º é o texto que já existia na tela). */
export const SUGESTOES_AVALIACAO: SugestaoTexto[] = [
  {
    rotulo: 'Cordial',
    texto: 'Olá {{cliente}}! Como foi o serviço no seu {{veiculo}}? Avalie em 1 clique: {{avaliacao}}',
  },
  {
    rotulo: 'Direta',
    texto: '{{cliente}}, de 1 a 5, que nota você dá para o serviço da O.S. {{numero}}? {{avaliacao}}',
  },
  {
    rotulo: 'Com agradecimento',
    texto:
      'Obrigado pela preferência, {{cliente}}! Sua opinião ajuda a gente a melhorar. Avalie o serviço no seu {{veiculo}}: {{avaliacao}}',
  },
];

/** Cliente ausente (só cliente e link). */
export const SUGESTOES_AUSENCIA: SugestaoTexto[] = [
  {
    rotulo: 'Cordial',
    texto:
      'Olá {{cliente}}! Faz um tempo que você não passa por aqui. Quando precisar, estamos à disposição — é só responder esta mensagem.',
  },
  {
    rotulo: 'Direta',
    texto: '{{cliente}}, que tal uma revisão? Responda esta mensagem para agendar.',
  },
  {
    rotulo: 'Com retorno',
    texto:
      'Olá {{cliente}}, sentimos sua falta! 🙂 Se o veículo precisar de uma olhada, chame a gente por aqui para agendar.',
  },
];

/** Campanha manual (cliente, veículo, placa e link). */
export const SUGESTOES_CAMPANHA: SugestaoTexto[] = [
  {
    rotulo: 'Cordial',
    texto:
      'Olá {{cliente}}! Passando para lembrar que estamos à disposição para cuidar do seu veículo. Para agendar, é só responder esta mensagem.',
  },
  {
    rotulo: 'Direta',
    texto: '{{cliente}}, agende sua revisão com a gente: é só responder esta mensagem.',
  },
  {
    rotulo: 'Pedir avaliação',
    texto: 'Olá {{cliente}}! Sua opinião é importante para nós. Se puder, avalie a oficina: {{link}}',
  },
];

/** Envio manual da OS pelo WhatsApp (o 1º é o padrão que já existia). */
export const SUGESTOES_ENVIO_OS: SugestaoTexto[] = [
  {
    rotulo: 'Cordial',
    texto:
      'Olá {{cliente}}! Finalizamos o serviço da sua OS {{numero}}: {{servicos}}. ' +
      'Total: {{total}}. Qualquer dúvida, estamos à disposição. 🙂',
  },
  {
    rotulo: 'Direta',
    texto: '{{cliente}}, sua OS {{numero}} está pronta. Total: {{total}}. Detalhes: {{acompanhamento}}',
  },
  {
    rotulo: 'Com itens',
    texto:
      'Olá {{cliente}}! Resumo da OS {{numero}}:\n{{itens}}\nTotal: {{total}}. Acompanhe: {{acompanhamento}}',
  },
];

/** Chaves {{…}} usadas num texto, normalizadas como o servidor faz. */
export function variaveisUsadas(texto: string): string[] {
  const chaves = new Set<string>();
  for (const m of texto.matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)) {
    chaves.add(m[1].trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''));
  }
  return [...chaves];
}
